import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import Button from '../ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { subscriptionService } from '../../services/subscriptionService';
import { paddleService } from '../../services/paddleService';
import { PLANS } from '../../config/plans';

const PortalContainer = styled.div`
  padding: ${theme.spacing.xxl};
  max-width: 900px;
  margin: 0 auto;
  font-family: ${theme.typography.fontFamily};
`;

const Header = styled.div`
  margin-bottom: ${theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: ${theme.typography.fontSizes.xxxl};
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.fontSizes.base};
  color: ${theme.colors.text};
  margin: 0;
  opacity: 0.8;
`;

const Section = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing.lg};
`;

const SectionTitle = styled.h2`
  font-size: ${theme.typography.fontSizes.lg};
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.md} 0;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing.sm} 0;
  border-bottom: 1px solid ${theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  color: ${theme.colors.text};
  font-size: ${theme.typography.fontSizes.base};
  opacity: 0.7;
`;

const InfoValue = styled.span`
  color: ${theme.colors.text};
  font-size: ${theme.typography.fontSizes.base};
  font-weight: ${theme.typography.fontWeights.medium};
`;

const StatusBadge = styled.span`
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSizes.sm};
  font-weight: ${theme.typography.fontWeights.bold};
  text-transform: uppercase;
  background: ${(props) => {
    switch (props.$status) {
      case 'active':
        return theme.colors.success;
      case 'trial':
        return theme.colors.primaryDark;
      case 'expired':
      case 'cancelled':
        return theme.colors.error;
      case 'past_due':
        return theme.colors.warning;
      default:
        return theme.colors.textMuted;
    }
  }};
  color: ${(props) => props.$status === 'active' ? theme.colors.background : 'white'};
`;

const UsageBar = styled.div`
  width: 100%;
  height: 20px;
  background: ${theme.colors.background};
  border-radius: ${theme.borderRadius.md};
  overflow: hidden;
  margin: ${theme.spacing.sm} 0;
`;

const UsageProgress = styled.div`
  height: 100%;
  background: ${(props) => {
    const percentage = props.$percentage;
    if (percentage >= 90) return theme.colors.error;
    if (percentage >= 75) return theme.colors.warning;
    return theme.colors.primary;
  }};
  width: ${(props) => Math.min(100, props.$percentage)}%;
  transition: width 0.3s ease;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.lg};
  flex-wrap: wrap;
`;

const Alert = styled.div`
  background: ${(props) => (props.$type === 'warning' ? 'rgba(255, 184, 108, 0.1)' : 'rgba(255, 85, 85, 0.1)')};
  border: 1px solid ${(props) => (props.$type === 'warning' ? theme.colors.warning : theme.colors.error)};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  color: ${theme.colors.text};
  font-size: ${theme.typography.fontSizes.base};
`;

const HelpText = styled.p`
  font-size: ${theme.typography.fontSizes.sm};
  color: ${theme.colors.text};
  margin: ${theme.spacing.md} 0 0 0;
  opacity: 0.7;
  line-height: 1.6;
`;

const ActionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.md};
`;

const ActionItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing.md};
  background: ${theme.colors.background};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border};
`;

const ActionLabel = styled.div`
  color: ${theme.colors.text};
  font-size: ${theme.typography.fontSizes.base};
`;

const ActionDescription = styled.div`
  color: ${theme.colors.text};
  font-size: ${theme.typography.fontSizes.sm};
  opacity: 0.7;
  margin-top: ${theme.spacing.xs};
`;

const ContactLink = styled.a`
  color: ${theme.colors.primary};
  text-decoration: none;
  font-weight: ${theme.typography.fontWeights.medium};

  &:hover {
    color: ${theme.colors.accent};
    text-decoration: underline;
  }
`;

const SmallButton = styled.button`
  background: ${props => props.$variant === 'primary'
    ? theme.colors.primaryDark
    : 'transparent'};
  color: ${props => props.$variant === 'primary' ? theme.colors.text : theme.colors.textSecondary};
  border: 1px solid ${theme.colors.border};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSizes.sm};
  font-weight: ${theme.typography.fontWeights.medium};
  cursor: pointer;
  font-family: inherit;
  transition: ${theme.transitions.normal};
  box-shadow: ${props => props.$variant === 'primary' ? theme.shadows.sm : 'none'};
  
  &:hover {
    background-color: ${props => props.$variant === 'primary' ? theme.colors.primary : theme.colors.surfaceHover};
    border-color: ${theme.colors.primary};
    color: ${theme.colors.text};
    box-shadow: ${theme.shadows.md};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  background: transparent;
  border: none;
  color: ${theme.colors.text};
  font-size: ${theme.typography.fontSizes.base};
  cursor: pointer;
  padding: ${theme.spacing.sm} 0;
  margin-bottom: ${theme.spacing.md};
  opacity: 0.7;
  font-family: inherit;

  &:hover {
    opacity: 1;
  }
`;

const BillingPortal = ({ onShowPlans, onBack, isFullPage = false }) => {
  const { user, subscription, userProfile, refreshSubscription } = useAuth();
  const [usageStats, setUsageStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgradeLoading, setUpgradeLoading] = useState(false);

  useEffect(() => {
    loadData();

    // Initialize Paddle for upgrades
    paddleService.initialize().catch(console.error);

    // Listen for checkout events
    const handleCheckoutCompleted = async () => {
      setUpgradeLoading(false);
      setTimeout(() => {
        refreshSubscription?.();
      }, 2000);
    };

    const handleCheckoutClosed = () => {
      setUpgradeLoading(false);
    };

    window.addEventListener('paddle-checkout-completed', handleCheckoutCompleted);
    window.addEventListener('paddle-checkout-closed', handleCheckoutClosed);

    return () => {
      window.removeEventListener('paddle-checkout-completed', handleCheckoutCompleted);
      window.removeEventListener('paddle-checkout-closed', handleCheckoutClosed);
    };
  }, [user, refreshSubscription]);

  const loadData = async () => {
    if (!user) return;

    try {
      const stats = await subscriptionService.getUsageStats(user.uid);
      setUsageStats(stats);
    } catch (error) {
      console.error('Error loading usage data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      let d;

      // Handle Firestore Timestamp (has toDate method)
      if (date?.toDate && typeof date.toDate === 'function') {
        d = date.toDate();
      }
      // Handle Firestore Timestamp object with seconds/nanoseconds
      else if (date?.seconds) {
        d = new Date(date.seconds * 1000);
      }
      // Handle Date object
      else if (date instanceof Date) {
        d = date;
      }
      // Handle ISO string or timestamp number
      else {
        d = new Date(date);
      }

      // Check if valid date
      if (isNaN(d.getTime())) return 'N/A';

      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch (e) {
      console.warn('Error formatting date:', e, date);
      return 'N/A';
    }
  };

  const getPlanName = (tier) => {
    if (!tier) return 'Free Trial';
    const plan = PLANS[tier];
    return plan ? plan.name : tier;
  };

  const getPlanPrice = (tier) => {
    if (!tier) return null;
    const plan = PLANS[tier];
    return plan ? `$${plan.price}/${plan.period}` : null;
  };

  const getUsagePercentage = () => {
    if (!usageStats || usageStats.isUnlimited) return 0;
    return usageStats.percentage || 0;
  };

  const handleUpgradeToPro = async () => {
    if (!user) return;

    setUpgradeLoading(true);
    try {
      // If user has existing subscription, update it instead of creating new
      if (subscription?.paddleSubscriptionId) {
        await paddleService.updateSubscription(subscription.paddleSubscriptionId, 'pro', user);
        // Refresh subscription data after update
        setTimeout(() => {
          refreshSubscription?.();
          setUpgradeLoading(false);
        }, 1000);
      } else {
        // No existing subscription, create new checkout
        await paddleService.openCheckout('pro', user);
      }
    } catch (error) {
      console.error('Error upgrading subscription:', error);
      alert('Failed to upgrade subscription: ' + error.message);
      setUpgradeLoading(false);
    }
  };

  const handleCancelRequest = () => {
    window.location.href = 'mailto:support@splitstep.ai?subject=Cancellation%20Request&body=I%20would%20like%20to%20cancel%20my%20subscription.';
  };

  const handleDowngradeRequest = () => {
    window.location.href = 'mailto:support@splitstep.ai?subject=Downgrade%20Request&body=I%20would%20like%20to%20downgrade%20from%20Pro%20to%20Starter.';
  };

  if (loading) {
    return (
      <PortalContainer>
        <Title>Loading...</Title>
      </PortalContainer>
    );
  }

  if (!subscription) {
    return (
      <PortalContainer>
        <Title>No Subscription Found</Title>
        <Alert $type="error">
          You don't have an active subscription. Please sign up for a plan to continue.
        </Alert>
        <Button onClick={onShowPlans}>View Plans</Button>
      </PortalContainer>
    );
  }

  const showUpgradeWarning = subscription.status === 'expired' || !subscription.canUseService;
  const usagePercentage = getUsagePercentage();
  const isTrialActive = subscription.status === 'trial' && subscription.isActive;
  const hasActiveSubscription = subscription.status === 'active' && subscription.paddleSubscriptionId;
  const isStarterPlan = subscription.tier === 'starter';
  const isProPlan = subscription.tier === 'pro';

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      window.location.href = '/';
    }
  };

  return (
    <PortalContainer>
      {isFullPage && (
        <BackButton onClick={handleBack}>
          ← Back to App
        </BackButton>
      )}
      <Header>
        <Title>Billing & Usage</Title>
        <Subtitle>Manage your subscription and track your usage</Subtitle>
      </Header>

      {showUpgradeWarning && (
        <Alert $type="error">
          Your {subscription.status === 'expired' ? 'trial has expired' : 'usage limit has been reached'}.
          Please upgrade to continue using the service.
        </Alert>
      )}

      {isTrialActive && subscription.trialEndDate && (
        <Alert $type="warning">
          Your free trial ends on {formatDate(subscription.trialEndDate)}. Subscribe now to continue
          without interruption.
        </Alert>
      )}

      {subscription.status === 'past_due' && (
        <Alert $type="error">
          Your payment failed. Please update your payment method to continue using the service.
          Check your email from Paddle for a payment update link.
        </Alert>
      )}

      {/* Current Plan */}
      <Section>
        <SectionTitle>Current Plan</SectionTitle>
        <InfoRow>
          <InfoLabel>Plan</InfoLabel>
          <InfoValue>{getPlanName(subscription.tier)}</InfoValue>
        </InfoRow>
        {getPlanPrice(subscription.tier) && (
          <InfoRow>
            <InfoLabel>Price</InfoLabel>
            <InfoValue>{getPlanPrice(subscription.tier)}</InfoValue>
          </InfoRow>
        )}
        <InfoRow>
          <InfoLabel>Status</InfoLabel>
          <StatusBadge $status={subscription.status}>{subscription.status}</StatusBadge>
        </InfoRow>
        {isTrialActive && subscription.trialEndDate && (
          <InfoRow>
            <InfoLabel>Trial Ends</InfoLabel>
            <InfoValue>{formatDate(subscription.trialEndDate)}</InfoValue>
          </InfoRow>
        )}
        {hasActiveSubscription && subscription.subscriptionEndDate && (
          <InfoRow>
            <InfoLabel>Renews On</InfoLabel>
            <InfoValue>{formatDate(subscription.subscriptionEndDate)}</InfoValue>
          </InfoRow>
        )}

        {/* Smart buttons based on current state */}
        <ButtonGroup>
          {/* Trial or no subscription - show Subscribe */}
          {(isTrialActive || showUpgradeWarning || !hasActiveSubscription) && (
            <Button onClick={onShowPlans}>
              Choose a Plan
            </Button>
          )}

          {/* Starter users - show Upgrade to Pro */}
          {hasActiveSubscription && isStarterPlan && (
            <Button
              onClick={handleUpgradeToPro}
              disabled={upgradeLoading}
              variant="primary"
            >
              {upgradeLoading ? 'Loading...' : 'Upgrade to Pro'}
            </Button>
          )}

          {/* Pro users - show View Plans (informational) */}
          {hasActiveSubscription && isProPlan && (
            <Button
              onClick={onShowPlans}
              variant="ghost"
            >
              View Plans
            </Button>
          )}
        </ButtonGroup>
      </Section>

      {/* Usage Statistics */}
      <Section>
        <SectionTitle>Usage This Month</SectionTitle>
        {usageStats && (
          <>
            <InfoRow>
              <InfoLabel>Objects Labeled</InfoLabel>
              <InfoValue>
                {usageStats.current.toLocaleString()}
                {!usageStats.isUnlimited && ` / ${usageStats.limit.toLocaleString()}`}
                {usageStats.isUnlimited && ' (Unlimited)'}
              </InfoValue>
            </InfoRow>
            {!usageStats.isUnlimited && (
              <>
                <UsageBar>
                  <UsageProgress $percentage={usagePercentage} />
                </UsageBar>
                <InfoRow>
                  <InfoLabel>Remaining</InfoLabel>
                  <InfoValue>{usageStats.remaining.toLocaleString()} labels</InfoValue>
                </InfoRow>
              </>
            )}
          </>
        )}
        {usagePercentage >= 75 && !usageStats?.isUnlimited && (
          <Alert $type="warning" style={{ marginTop: theme.spacing.md, marginBottom: 0 }}>
            You've used {usagePercentage.toFixed(0)}% of your monthly limit. Consider upgrading to
            Pro for unlimited labels.
          </Alert>
        )}
      </Section>

      {/* Manage Subscription - show for active subscribers */}
      {hasActiveSubscription && (
        <Section>
          <SectionTitle>Manage Subscription</SectionTitle>
          <ActionList>
            {/* Upgrade option for Starter */}
            {isStarterPlan && (
              <ActionItem>
                <div>
                  <ActionLabel>Upgrade to Pro</ActionLabel>
                  <ActionDescription>Get unlimited labels and priority support</ActionDescription>
                </div>
                <SmallButton $variant="primary" onClick={handleUpgradeToPro} disabled={upgradeLoading}>
                  {upgradeLoading ? 'Loading...' : 'Upgrade'}
                </SmallButton>
              </ActionItem>
            )}

            {/* Downgrade option for Pro */}
            {isProPlan && (
              <ActionItem>
                <div>
                  <ActionLabel>Downgrade to Starter</ActionLabel>
                  <ActionDescription>Switch to {PLANS.starter.usageLimit.toLocaleString()} labels/month at ${PLANS.starter.price}/{PLANS.starter.period}</ActionDescription>
                </div>
                <SmallButton onClick={handleDowngradeRequest}>
                  Request Downgrade
                </SmallButton>
              </ActionItem>
            )}

            {/* Update payment */}
            <ActionItem>
              <div>
                <ActionLabel>Update Payment Method</ActionLabel>
                <ActionDescription>Check your email for a management link from Paddle</ActionDescription>
              </div>
            </ActionItem>

            {/* Cancel */}
            <ActionItem>
              <div>
                <ActionLabel>Cancel Subscription</ActionLabel>
                <ActionDescription>Your access continues until the end of your billing period</ActionDescription>
              </div>
              <SmallButton onClick={handleCancelRequest}>
                Request Cancellation
              </SmallButton>
            </ActionItem>
          </ActionList>
        </Section>
      )}

      {/* Account Information */}
      <Section>
        <SectionTitle>Account</SectionTitle>
        <InfoRow>
          <InfoLabel>Email</InfoLabel>
          <InfoValue>{user?.email}</InfoValue>
        </InfoRow>
        {userProfile?.displayName && (
          <InfoRow>
            <InfoLabel>Name</InfoLabel>
            <InfoValue>{userProfile.displayName}</InfoValue>
          </InfoRow>
        )}
        <InfoRow>
          <InfoLabel>Member Since</InfoLabel>
          <InfoValue>
            {formatDate(
              userProfile?.createdAt ||
              userProfile?.subscriptionStartDate ||
              user?.metadata?.creationTime
            )}
          </InfoValue>
        </InfoRow>
      </Section>

      {/* Help */}
      <Section>
        <SectionTitle>Need Help?</SectionTitle>
        <HelpText style={{ margin: 0 }}>
          For billing questions contact us at{' '}
          <ContactLink href="mailto:support@splitstep.ai">support@splitstep.ai</ContactLink>
        </HelpText>
      </Section>
    </PortalContainer>
  );
};

export default BillingPortal;
